import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";
import { COMPLEXITY_ITEMS } from "@/data/content";
import { EASE } from "@/lib/motion";
import StatePicker from "@/components/StatePicker";

const inputCls =
  "w-full border border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";

export default function EnquiryForm({ type = "general", showSituation = false, initialItems = [], initialStates = [], heading, subheading }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [selectedIds, setSelectedIds] = useState(initialItems);
  const [states, setStates] = useState(initialStates);
  const [status, setStatus] = useState("idle");

  const toggleItem = (id) =>
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    const labels = COMPLEXITY_ITEMS.filter((i) => selectedIds.includes(i.id)).map((i) => i.label);
    if (states.length > 0) labels.push(`Multiple states (${states.join(", ")})`);
    try {
      await submitEnquiry({
        ...form,
        enquiry_type: type,
        situation: labels,
        situation_items: selectedIds,
        states,
      });
      setStatus("done");
      toast.success("Received — we'll be in touch shortly.");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong sending your enquiry. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <motion.div
        data-testid="enquiry-success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="border border-steel-brass/40 bg-steel-surface p-10"
      >
        <div className="flex h-12 w-12 items-center justify-center border border-steel-brass/50">
          <Check className="h-5 w-5 text-steel-brass" />
        </div>
        <h3 className="mt-6 font-display text-2xl font-medium text-steel-text">Received, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-sm leading-relaxed text-steel-secondary">
          Your enquiry is in. We review every message personally and typically respond within one business
          day. Keep an eye on {form.email}.
        </p>
        <button
          data-testid="enquiry-reset-btn"
          onClick={() => {
            setForm({ name: "", email: "", phone: "", message: "" });
            setSelectedIds([]);
            setStates([]);
            setStatus("idle");
          }}
          className="mt-8 text-sm font-medium text-steel-brass transition-colors hover:text-steel-text"
        >
          Send another message
        </button>
      </motion.div>
    );
  }

  return (
    <div>
      {(heading || subheading) && (
        <div className="mb-8">
          {heading && <h3 className="font-display text-2xl font-medium text-steel-text">{heading}</h3>}
          {subheading && <p className="mt-2 text-sm text-steel-secondary">{subheading}</p>}
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-5" data-testid="enquiry-form">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Full name *</label>
            <input
              data-testid="enquiry-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
              placeholder="Jane Citizen"
            />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Email *</label>
            <input
              data-testid="enquiry-email-input"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
              placeholder="jane@example.com"
            />
          </div>
        </div>
        <div>
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Phone (optional)</label>
          <input
            data-testid="enquiry-phone-input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className={inputCls}
            placeholder="(555) 000-0000"
          />
        </div>
        {showSituation && (
          <div>
            <label className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">
              What does your tax situation include?
            </label>
            <div className="flex flex-wrap gap-2">
              {COMPLEXITY_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  data-testid={`situation-chip-${item.id}`}
                  onClick={() => toggleItem(item.id)}
                  className={`border px-3 py-1.5 text-xs transition-colors duration-300 ${
                    selectedIds.includes(item.id)
                      ? "border-steel-accent bg-steel-accent/15 text-steel-text"
                      : "border-steel-border text-steel-secondary hover:border-steel-muted hover:text-steel-text"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="mt-4">
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">
                States you'll need filed
              </label>
              <StatePicker selected={states} onChange={setStates} />
            </div>
          </div>
        )}
        <div>
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">
            Anything we should know?
          </label>
          <textarea
            data-testid="enquiry-message-input"
            rows={5}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className={`${inputCls} resize-none`}
            placeholder="Please don't include Social Security numbers or account numbers here — those move through the secure portal later."
          />
        </div>
        <button
          data-testid="enquiry-submit-button"
          type="submit"
          disabled={status === "sending"}
          className="group flex w-full items-center justify-center gap-2 bg-steel-text px-8 py-4 text-sm font-semibold tracking-wide text-steel-bg transition-colors duration-300 hover:bg-zinc-300 disabled:opacity-60 sm:w-auto"
        >
          {status === "sending" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending…
            </>
          ) : (
            <>
              Send Enquiry
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </>
          )}
        </button>
      </form>
      <AnimatePresence />
    </div>
  );
}
